// The boxes of the New Quotation form. Each takes the whole form and a `set` that changes part of it.
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutlineOutlined';
import { Panel } from '../../../components/Panel';
import { amount } from '../../../lib/money';
import { SAMPLE_DATA } from '../../../lib/sample';
import { useFormLists } from '../../shipments/useShipments';
import { STANDARD_CONDITIONS } from './standardConditions';
import { FormField as Field } from '../../../components/FormField';
import {
  chargeTotal,
  fillFromShipment,
  INCOTERMS,
  MODES,
  NEW_CARGO,
  NEW_CHARGE,
  totalPrice,
  UNITS,
  type QuoteForm,
} from './quoteForm';

export type SectionProps = { form: QuoteForm; set: (patch: Partial<QuoteForm>) => void };

const three = {
  display: 'grid',
  gap: 1.5,
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' },
};

export function CustomerSection({ form, set }: SectionProps) {
  const { customerNames, shipments } = useFormLists();
  return (
    <Panel title="Customer Information">
      <Box sx={three}>
        <Field
          label="Customer"
          required
          value={form.customer}
          onChange={(customer) => set({ customer })}
          options={customerNames}
        />
        <Field label="Contact Person" value={form.contact} onChange={(contact) => set({ contact })} />
        <Field label="Email" type="email" value={form.email} onChange={(email) => set({ email })} />
        <Field label="Quotation Number" required value={form.number} onChange={(number) => set({ number })} />
        <Field label="Quotation Date" required type="date" value={form.date} onChange={(date) => set({ date })} />
        <Field
          label="Quote Validity"
          required
          type="date"
          value={form.validity}
          onChange={(validity) => set({ validity })}
        />
        <Field
          label="Related SRB (optional)"
          value={form.relatedSrb}
          onChange={(code) => {
            const s = shipments.find((x) => x.srb === code);
            set(s ? fillFromShipment(form, s) : { relatedSrb: code });
          }}
          options={shipments.map((x) => x.srb)}
        />
      </Box>
    </Panel>
  );
}

export function RouteSection({ form, set }: SectionProps) {
  const { places, cargoTypes } = useFormLists();
  return (
    <Panel title="Route & Transport Details">
      <Box sx={three}>
        <Field label="Origin" required value={form.origin} onChange={(origin) => set({ origin })} options={places} />
        <Field
          label="Destination"
          required
          value={form.destination}
          onChange={(destination) => set({ destination })}
          options={places}
        />
        <Field
          label="Transit Time"
          value={form.transit}
          onChange={(transit) => set({ transit })}
          placeholder="e.g. 7 days"
        />
        <Field label="Mode" required value={form.mode} onChange={(mode) => set({ mode })} options={MODES} />
        <Field label="Type" required value={form.type} onChange={(type) => set({ type })} options={cargoTypes} />
        <Field label="Incoterm" value={form.incoterm} onChange={(incoterm) => set({ incoterm })} options={INCOTERMS} />
        <Field label="Carrier" value={form.carrier} onChange={(carrier) => set({ carrier })} />
        <Field label="Cut Off Date" type="date" value={form.cutOff} onChange={(cutOff) => set({ cutOff })} />
        <Field
          label="Sailing Date (or Departure)"
          type="date"
          value={form.sailing}
          onChange={(sailing) => set({ sailing })}
        />
      </Box>
    </Panel>
  );
}

const cargoRow = {
  display: 'grid',
  gap: 1,
  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr)) 40px' },
  alignItems: 'center',
};

export function ShipmentSection({ form, set }: SectionProps) {
  const change = (i: number, patch: Partial<(typeof form.cargo)[number]>) =>
    set({ cargo: form.cargo.map((c, k) => (k === i ? { ...c, ...patch } : c)) });
  return (
    <Panel title="Shipment Information">
      <Stack spacing={1.5}>
        {form.cargo.map((c, i) => (
          <Box key={i} sx={cargoRow}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField
                label="Description"
                value={c.description}
                onChange={(e) => change(i, { description: e.target.value })}
              />
            </Box>
            <TextField
              label="Containers"
              value={c.containers}
              onChange={(e) => change(i, { containers: e.target.value })}
            />
            <TextField
              label="Dimensions (cm)"
              value={c.dimensions}
              onChange={(e) => change(i, { dimensions: e.target.value })}
            />
            <TextField
              label="Chargeable Weight (kg)"
              value={c.chargeable}
              onChange={(e) => change(i, { chargeable: e.target.value })}
            />
            <TextField
              label="Gross Weight (kg)"
              value={c.gross}
              onChange={(e) => change(i, { gross: e.target.value })}
            />
            <IconButton
              aria-label="Remove item"
              disabled={form.cargo.length === 1}
              onClick={() => set({ cargo: form.cargo.filter((_c, k) => k !== i) })}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Box>
          <Button startIcon={<AddIcon />} onClick={() => set({ cargo: [...form.cargo, NEW_CARGO] })}>
            Add Another Item
          </Button>
        </Box>
      </Stack>
    </Panel>
  );
}

const chargeRow = {
  display: 'grid',
  gap: 1,
  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: '3fr 1.2fr 1fr 1fr 40px' },
  alignItems: 'center',
};

export function ChargesSection({ form, set }: SectionProps) {
  const change = (i: number, patch: Partial<(typeof form.charges)[number]>) =>
    set({ charges: form.charges.map((c, k) => (k === i ? { ...c, ...patch } : c)) });
  return (
    <Panel title="Charges Description" subtitle="Per kg is multiplied by the chargeable weight above.">
      <Stack spacing={1.5}>
        {form.charges.map((c, i) => (
          <Box key={i} sx={chargeRow}>
            <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
              <TextField label="Item" value={c.item} onChange={(e) => change(i, { item: e.target.value })} />
            </Box>
            <Field
              label="Units"
              value={c.units}
              onChange={(units) => change(i, { units: units as typeof c.units })}
              options={UNITS}
            />
            <TextField
              label="Unit Price (USD)"
              value={c.unitPrice}
              onChange={(e) => change(i, { unitPrice: e.target.value })}
            />
            <Typography sx={{ textAlign: 'right', fontWeight: 600 }}>{amount(chargeTotal(c, form))}</Typography>
            <IconButton
              aria-label="Remove charge"
              disabled={form.charges.length === 1}
              onClick={() => set({ charges: form.charges.filter((_c, k) => k !== i) })}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Button startIcon={<AddIcon />} onClick={() => set({ charges: [...form.charges, NEW_CHARGE] })}>
            Add Charge
          </Button>
          <Typography sx={{ fontWeight: 750, fontSize: 20 }}>
            Total Price (USD){' '}
            <Box component="span" sx={{ color: 'primary.main', ml: 1 }}>
              {amount(totalPrice(form))}
            </Box>
          </Typography>
        </Stack>
      </Stack>
    </Panel>
  );
}

export function ConditionsSection({ form, set }: SectionProps) {
  return (
    <Panel
      title="General Conditions"
      subtitle="These standard conditions are always printed on the quotation. Add anything extra below."
    >
      <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
        {STANDARD_CONDITIONS.map((c) => (
          <Typography key={c} component="li" variant="body2">
            {c}
          </Typography>
        ))}
      </Box>
      {SAMPLE_DATA && (
        <Typography variant="caption" color="warning.main">
          Sample wording until the real text from the Word template is added.
        </Typography>
      )}
      <TextField
        multiline
        minRows={3}
        label="Additional conditions (optional)"
        placeholder="Printed after the standard conditions"
        value={form.conditions}
        onChange={(e) => set({ conditions: e.target.value })}
      />
    </Panel>
  );
}
